import { useEffect, useState } from "react";

import { useQuery } from "@apollo/client";
import { Classes, Colors } from "@blueprintjs/core";
import { useParams } from "react-router";

import { Box, Flex } from "@/components/layout/flexbox";
import { AddToCollectionDialog } from "@/components/pages_logged_in/roleplays/AddToCollectionDialog";
import { useScenariosLazyQuery } from "@/components/pages_logged_in/roleplays/api";
import { EditScenario } from "@/components/pages_logged_in/roleplays/details/scenario/edit/EditScenario";
import { ScenariosPageType } from "@/components/pages_logged_in/roleplays/scenarios/const";
import { ScenariosListHeader } from "@/components/pages_logged_in/roleplays/scenarios/ScenarioListHeader";
import Paging from "@/components/pages_logged_in/workspace-admin/Paging";
import { AddButton } from "@/components/pieces/button/AddButton";
import CloneScenarioDialog from "@/components/pieces/roleplays/CloneScenarioDialog";
import ScenarioCard from "@/components/pieces/roleplays/ScenarioCard";
import ScenarioDeleteConfirm from "@/components/pieces/roleplays/ScenarioDeleteConfirm";
import { ScenarioModals } from "@/components/pieces/roleplays/ScenarioMenu";
import { SUBTEXT_COLOR } from "@/css/constants";
import { RoleplayDocument, QueryScenariosOrderBy } from "@/graphql";

const PAGE_SIZE = 10;

export default function ScenariosList({
  type = ScenariosPageType.Assigned,
  title = "",
  pageLoading = false,
  statusFilter = null,
  scopeFilter = null,
  showShortDesc = false,
  showRank = true,
  showDifficulty = true,
  showRoleplay = true,
  showScope = false,
  showAction = false,
  iconSize = 100,
  allowClickOnCard = true,
  allowAdd = false,
  showPlayButton = true,
  onSelect = null,
  pageSize = PAGE_SIZE,
  selectedScenarioIds = [],
  loadingItem = null,
}) {
  const [page, setPage] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState(statusFilter);
  const [selectedScope, setSelectedScope] = useState(scopeFilter);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [currentActiveModal, setCurrentActiveModal] =
    useState<ScenarioModals | null>(null);
  const [query, setQuery] = useState("");
  const roleplaySlug = useParams<{
    roleplaySlug?: string;
  }>()?.roleplaySlug;

  const { data: roleplayData } = useQuery(RoleplayDocument, {
    variables: {
      slug: roleplaySlug,
    },
    skip: !roleplaySlug,
  });

  const roleplay = roleplayData?.roleplay;

  const {
    scenariosData,
    loading,
    queryPersonalScenarios,
    queryAssignedScenarios,
    queryAllScenarios,
  } = useScenariosLazyQuery({
    onCompleted: () => {},
    onError: () => {},
  });

  const { data = [], totalRecords = 0 } = scenariosData || {};

  const fetchScenarios =
    type == ScenariosPageType.All
      ? queryAllScenarios
      : type == ScenariosPageType.Assigned
        ? queryAssignedScenarios
        : queryPersonalScenarios;

  const handleFetchScenarios = () => {
    const scope = selectedScope === -1 ? null : selectedScope;
    fetchScenarios({
      page,
      pageSize,
      status: selectedStatus,
      scope,
      query,
      orderBy: QueryScenariosOrderBy.CreatedAtDesc,
    });
  };
  useEffect(() => {
    handleFetchScenarios();
  }, [page, selectedStatus, selectedScope, query]);

  const loadingBody = (
    <Box>
      <Box height={100} className={Classes.SKELETON} mt={0} />
      <Box height={100} className={Classes.SKELETON} mt={24} />
      <Box height={100} className={Classes.SKELETON} mt={24} />
      <Box height={100} className={Classes.SKELETON} mt={24} />
      <Box height={100} className={Classes.SKELETON} mt={24} />
    </Box>
  );

  const emptyBody = (
    <Flex flexWrap="wrap" alignItems="center" justifyContent="center">
      <Box color={SUBTEXT_COLOR}>No Scenarios</Box>
    </Flex>
  );

  return (
    <Box>
      <ScenariosListHeader
        title={title}
        loading={pageLoading}
        allowAdd={allowAdd}
        statusFilter={selectedStatus}
        scopeFilter={selectedScope}
        onChangeStatusFilter={(value) => {
          setSelectedStatus(value);
          setPage(1);
        }}
        onChangeScopeFilter={(value) => {
          setSelectedScope(value);
          setPage(1);
        }}
        onSearch={(value) => {
          setQuery(value);
          setPage(1);
        }}
      />
      {loading || pageLoading ? (
        loadingBody
      ) : (
        <Box mb={24}>
          {data?.length === 0 ? emptyBody : null}
          {data.map((scenario, index) => {
            const isSelected = selectedScenarioIds.includes(scenario.id);
            const isLoading =
              loadingItem?.loading && loadingItem?.id === scenario.id;
            return (
              <Box mt={index > 0 ? 24 : 0} key={scenario.id}>
                <ScenarioCard
                  scenario={scenario}
                  roleplay={roleplay}
                  showShortDesc={showShortDesc}
                  showRank={showRank}
                  showDifficulty={showDifficulty}
                  showRoleplay={showRoleplay}
                  iconSize={iconSize}
                  allowClickOnCard={allowClickOnCard}
                  showPlayButton={showPlayButton}
                  showScope={showScope}
                  showAction={showAction}
                  rightButton={
                    onSelect ? (
                      <AddButton
                        isAdded={isSelected}
                        isLoading={isLoading}
                        onAdd={() => onSelect(scenario)}
                        onRemove={() => onSelect(scenario)}
                      />
                    ) : null
                  }
                  onDelete={() => {
                    setCurrentActiveModal(ScenarioModals.deleteScenario);
                    setSelectedScenario(scenario);
                  }}
                  onCloneScenario={() => {
                    setCurrentActiveModal(ScenarioModals.cloneScenario);
                    setSelectedScenario(scenario);
                  }}
                  onAddToCollection={() => {
                    setCurrentActiveModal(ScenarioModals.addToCollection);
                    setSelectedScenario(scenario);
                  }}
                  onEditScenario={() => {
                    setCurrentActiveModal(ScenarioModals.editScenario);
                    setSelectedScenario(scenario);
                  }}
                />
              </Box>
            );
          })}
        </Box>
      )}
      <Paging
        totalRecords={totalRecords}
        pageSize={pageSize}
        currentPage={page}
        setCurrentPage={setPage}
        isSticky={true}
        style={{
          background: Colors.WHITE,
          padding: 10,
          marginTop: 20,
        }}
      />
      <ScenarioDeleteConfirm
        open={currentActiveModal === ScenarioModals.deleteScenario}
        scenario={selectedScenario}
        onClose={() => setCurrentActiveModal(null)}
        onSuccess={() => {
          handleFetchScenarios();
          setCurrentActiveModal(null);
        }}
        shouldRedirectAfterDelete={false}
      />
      <CloneScenarioDialog
        open={currentActiveModal === ScenarioModals.cloneScenario}
        onClose={() => setCurrentActiveModal(null)}
        scenario={selectedScenario}
      />
      <AddToCollectionDialog
        open={currentActiveModal === ScenarioModals.addToCollection}
        onClose={() => setCurrentActiveModal(null)}
        scenario={selectedScenario}
      />
      <EditScenario
        key={selectedScenario?.id}
        open={currentActiveModal === ScenarioModals.editScenario}
        onClose={() => setCurrentActiveModal(null)}
        scenario={selectedScenario}
      />
    </Box>
  );
}
